import { AIEditOptions, FlexBox } from '@airship-platform/jam-web/components';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Controller, useFormContext } from 'react-hook-form';
import { z } from 'zod';
import {
  QuoteContext,
  RewriteOption,
  useAIGenerator,
  useToast,
} from '../../hooks';

export const ProposalPreferencesSchema = z.object({
  proposalPreferences: z.object({
    quote: z.string(),
  }),
});

type ProposalPreferences = z.infer<typeof ProposalPreferencesSchema>;

const ProfileBioInput: React.FC = () => {
  const { error, isLoadingOption, generateQuote } = useAIGenerator();

  const { showToast } = useToast();

  const { control, watch, setValue, clearErrors } =
    useFormContext<ProposalPreferences>();

  const currentQuote = watch('proposalPreferences.quote');

  const improveBioHandler = async (rewriteOption: RewriteOption) => {
    if (!currentQuote?.trim()) {
      control.setError('proposalPreferences.quote', {
        type: 'manual',
        message: 'Please enter a bio to improve',
      });
      return;
    }

    clearErrors('proposalPreferences.quote');

    const result = await generateQuote(
      currentQuote,
      rewriteOption,
      QuoteContext.BIO
    );
    if (result.length) {
      setValue(
        'proposalPreferences.quote',
        result.map((r) => r.improvedQuote).join(' ')
      );
      showToast('Message has been improved!', 'success');
    } else {
      showToast('We were unable to generate an improved message', 'warning');
    }
  };

  return (
    <FlexBox flexDirection="column" gap={2}>
      <Controller
        name="proposalPreferences.quote"
        control={control}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            label={'Profile Bio (displays on customer proposals)'}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            multiline
            rows={8}
            fullWidth
            disabled={!!isLoadingOption}
          />
        )}
      />

      <FlexBox flexDirection={'column'} gap={1} width={'100%'}>
        <Typography variant="subtitle2">Improve bio with AI</Typography>
        <FlexBox flexDirection={'row'} gap={1} flexWrap="wrap">
          <AIEditOptions
            onClick={improveBioHandler}
            disabled={!!isLoadingOption}
            isLoadingOption={isLoadingOption}
          />
        </FlexBox>
        {error && (
          <Typography variant="body3" color="error">
            There was an error generating the message
          </Typography>
        )}
      </FlexBox>
    </FlexBox>
  );
};

export default ProfileBioInput;
